"use client";

import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Loader2,
  RefreshCw,
  Save,
  Sparkles,
  TrendingUp,
} from "lucide-react";
import { AdminShell } from "@/components/admin-shell";
import { FgCard } from "@/components/fg-card";
import { FgButton } from "@/components/fg-button";
import { FgInput } from "@/components/fg-input";
import { FgToast } from "@/components/fg-toast";
import { Bento, PageHero, Pill, Widget } from "@/components/Widget";
import { Sparkline } from "@/components/ai-model-pricing/Sparkline";
import { StepByStepGuide } from "@/components/ai-model-pricing/StepByStepGuide";
import { aiModelPricingApi, ModelStatusEntry } from "@/lib/aiModelPricing";

export function AiModelPricingPage() {
  const qc = useQueryClient();
  const [guideFor, setGuideFor] = useState<{ tier: string; open: boolean }>({ tier: "", open: false });
  const [editingTier, setEditingTier] = useState<{ name: string; value: string } | null>(null);

  const status = useQuery({
    queryKey: ["ai-model-pricing", "status"],
    queryFn: aiModelPricingApi.status,
    refetchInterval: 60_000,
  });

  const recs = useQuery({
    queryKey: ["ai-model-pricing", "recommendations"],
    queryFn: aiModelPricingApi.recommendations,
  });

  const probeNow = useMutation({
    mutationFn: (modelId: string) => aiModelPricingApi.probeNow(modelId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ai-model-pricing"] });
      FgToast.success("Probe complete");
    },
    onError: (e: any) => FgToast.error(`Probe failed: ${e?.message ?? "unknown"}`),
  });

  const updateMargin = useMutation({
    mutationFn: ({ tierName, targetMarginPercent }: { tierName: string; targetMarginPercent: number }) =>
      aiModelPricingApi.updateMargin(tierName, targetMarginPercent),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ai-model-pricing"] });
      FgToast.success("Margin saved — recommendations refreshed");
      setEditingTier(null);
    },
    onError: (e: any) => FgToast.error(`Save failed: ${e?.message ?? "unknown"}`),
  });

  const stalenessWarning = useMemo(() => {
    const stale = (status.data?.models ?? []).find(
      (m) => (m.daysSinceLastProbe ?? 0) > 1
    );
    return stale ? `Last probe of ${stale.modelId} was ${stale.daysSinceLastProbe} days ago — daily cron is overdue.` : null;
  }, [status.data]);

  const formatUsd = (cents: number, currency: string) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency }).format(cents / 100);

  return (
    <AdminShell>
      <Bento>
        <PageHero
          accent="blue"
          eyebrow="Gemini cost control"
          title="AI Model & Pricing"
          description="Live Gemini model status, probe history, and margin-aware per-tier pricing recommendations."
          aside={
            <span className="fg-pill-blue">
              <Pill accent="blue">
                <Sparkles size={12} />
                {status.data?.models?.length ?? 0} models
              </Pill>
            </span>
          }
        />

        {stalenessWarning && (
          <Widget accent="yellow" title="Stale probe">
            <div className="flex items-start gap-3 text-sm text-[#FFD60A]">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{stalenessWarning}</span>
            </div>
          </Widget>
        )}

        {/* ── Section A — Model cards ────────────────────────────────────── */}
        <section>
          <h3 className="text-xs font-mono uppercase tracking-widest text-[#8E8E93] mb-3">
            Configured models
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {status.data?.models?.map((m) => (
              <ModelCard
                key={m.modelId}
                model={m}
                probing={probeNow.isPending && probeNow.variables === m.modelId}
                onProbe={() => probeNow.mutate(m.modelId)}
              />
            ))}
            {status.isLoading && (
              <div className="col-span-2 flex items-center gap-2 text-[#8E8E93] text-sm py-6 justify-center">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading model status…
              </div>
            )}
          </div>
        </section>

        {/* ── Section C — Recommendations ────────────────────────────── */}
        <section>
          <h3 className="text-xs font-mono uppercase tracking-widest text-[#8E8E93] mb-3">
            Per-tier pricing recommendations
          </h3>
          <FgCard>
            <div className="overflow-x-auto -mx-2">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] font-mono uppercase tracking-wider text-[#8E8E93] text-left">
                    <th className="px-3 py-2">Tier</th>
                    <th className="px-3 py-2">Current</th>
                    <th className="px-3 py-2">Avg cost / user / mo</th>
                    <th className="px-3 py-2">Target margin</th>
                    <th className="px-3 py-2">Recommended</th>
                    <th className="px-3 py-2">Δ</th>
                    <th className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {recs.data?.recommendations?.map((r) => {
                    const isEditing = editingTier?.name === r.tierName;
                    return (
                      <tr key={r.tierName} className="border-t border-[rgba(255,255,255,0.05)]">
                        <td className="px-3 py-2 font-mono font-bold text-[#FAFAFA]">{r.tierName}</td>
                        <td className="px-3 py-2 text-[#FAFAFA]">{formatUsd(r.currentPriceCents, r.currency)}</td>
                        <td className="px-3 py-2 text-[#8E8E93] font-mono">
                          ${r.avgCostPerUserMonthUsd.toFixed(4)}
                        </td>
                        <td className="px-3 py-2 font-mono text-[#FAFAFA]">
                          {isEditing ? (
                            <div className="flex items-center gap-2">
                              <FgInput
                                type="number"
                                value={editingTier.value}
                                onChange={(e) =>
                                  setEditingTier({ name: r.tierName, value: e.target.value })
                                }
                                className="w-20"
                              />
                              <span>%</span>
                            </div>
                          ) : (
                            `${r.targetMarginPercent.toFixed(0)}%`
                          )}
                        </td>
                        <td className="px-3 py-2 font-bold text-[#FFD60A] font-mono">
                          {formatUsd(r.recommendedPriceCents, r.currency)}
                        </td>
                        <td className="px-3 py-2 font-mono">
                          <span
                            className={
                              r.deltaCents > 0
                                ? "text-[#E63946]"
                                : r.deltaCents < 0
                                ? "text-emerald-400"
                                : "text-[#8E8E93]"
                            }
                          >
                            {r.deltaCents > 0 ? "+" : ""}
                            {formatUsd(r.deltaCents, r.currency)}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right space-x-2">
                          {isEditing ? (
                            <>
                              <FgButton
                                size="sm"
                                variant="primary"
                                onClick={() =>
                                  updateMargin.mutate({
                                    tierName: r.tierName,
                                    targetMarginPercent: Number(editingTier.value),
                                  })
                                }
                                disabled={updateMargin.isPending}
                              >
                                {updateMargin.isPending ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <Save className="w-3 h-3" />
                                )}
                                Save
                              </FgButton>
                              <FgButton size="sm" variant="ghost" onClick={() => setEditingTier(null)}>
                                Cancel
                              </FgButton>
                            </>
                          ) : (
                            <>
                              <FgButton
                                size="sm"
                                variant="ghost"
                                onClick={() =>
                                  setEditingTier({
                                    name: r.tierName,
                                    value: r.targetMarginPercent.toString(),
                                  })
                                }
                              >
                                Edit margin
                              </FgButton>
                              <FgButton
                                size="sm"
                                variant="primary"
                                onClick={() => setGuideFor({ tier: r.tierName, open: true })}
                              >
                                View steps
                              </FgButton>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {recs.isLoading && (
                    <tr>
                      <td colSpan={7} className="px-3 py-6 text-center text-[#8E8E93]">
                        <Loader2 className="inline-block w-4 h-4 animate-spin mr-2" />
                        Computing recommendations…
                      </td>
                    </tr>
                  )}
                  {!recs.isLoading && (recs.data?.recommendations?.length ?? 0) === 0 && (
                    <tr>
                      <td colSpan={7} className="px-3 py-6 text-center text-[#8E8E93]">
                        No tiers configured yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="mt-3 text-[11px] text-[#8E8E93] font-mono">
              Avg cost = mean of (sum of <code className="text-[#FFD60A]">ai_cost_logs</code> grouped by user) over the last 30 days. Recommended price = ceil(avg_cost / (1 − target_margin/100)).
            </div>
          </FgCard>
        </section>

        <div className="text-[11px] text-[#8E8E93]">
          Edit <code className="text-[#FAFAFA]">docs_group_B/gemini-model-reference.json</code> to push <code className="text-[#FAFAFA]">availableUntil</code> updates — the next probe picks them up automatically.
        </div>
      </Bento>

      <StepByStepGuide
        tier={guideFor.tier}
        open={guideFor.open}
        onClose={() => setGuideFor((g) => ({ ...g, open: false }))}
      />
    </AdminShell>
  );
}

function ModelCard({
  model,
  probing,
  onProbe,
}: {
  model: ModelStatusEntry;
  probing: boolean;
  onProbe: () => void;
}) {
  const { data: history } = useQuery({
    queryKey: ["ai-model-pricing", "history", model.modelId],
    queryFn: () => aiModelPricingApi.history(model.modelId, 30),
    refetchInterval: 60_000,
  });

  const badgeColor =
    model.status === "live"
      ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
      : model.status === "degraded"
      ? "bg-[#FFD60A]/15 text-[#FFD60A] border-[#FFD60A]/30"
      : "bg-[#E63946]/15 text-[#E63946] border-[#E63946]/30";

  return (
    <FgCard>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-mono text-sm font-bold text-[#FAFAFA] truncate">
            {model.modelId}
          </div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-[#8E8E93] mt-1">
            {model.category === "text" ? "Text" : model.category === "image" ? "Image" : "—"}
            {model.currentPricePerMTokensUsd != null &&
              ` · $${model.currentPricePerMTokensUsd}/1M in`}
            {model.currentPricePerImageUsd != null &&
              ` · $${model.currentPricePerImageUsd}/img`}
          </div>
        </div>
        <span
          className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${badgeColor}`}
        >
          {model.status === "live" ? (
            <CheckCircle2 className="w-3 h-3" />
          ) : model.status === "degraded" ? (
            <Clock className="w-3 h-3" />
          ) : (
            <AlertTriangle className="w-3 h-3" />
          )}
          {model.status}
        </span>
      </div>

      <div className="mt-4">
        <Sparkline history={history ?? []} height={64} />
      </div>

      <div className="mt-4 flex items-center justify-between">
        <div className="text-[11px] font-mono text-[#8E8E93]">
          {model.lastProbedAt
            ? `Last probe ${model.daysSinceLastProbe ?? 0}d ago · ${model.lastLatencyMs ?? "?"} ms`
            : "Never probed yet"}
          {model.availableUntil && (
            <div className="mt-1 text-[#FFD60A]">
              <TrendingUp className="inline w-3 h-3 mr-1" />
              available until {model.availableUntil}
              {model.replacement && (
                <span className="text-[#8E8E93]"> → {model.replacement}</span>
              )}
            </div>
          )}
        </div>
        <FgButton size="sm" variant="ghost" onClick={onProbe} disabled={probing}>
          {probing ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <RefreshCw className="w-3 h-3" />
          )}
          Refresh
        </FgButton>
      </div>
    </FgCard>
  );
}
